import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CalendarCheck, Clock, Dumbbell, Flame, Layers, Play, RotateCcw, Trash2 } from 'lucide-react';
import { Chip, EmptyState, MediaBox } from './parts';
import { estimateMinutes, plural, progressOf, safeMediaUrl, supersetOf, whyText } from './playerLogic';

// "O meu treino de hoje": o que há para fazer, quanto demora, e UM botão grande — "Começar treino", ou "Continuar treino" se há um em curso.

const Thumb = ({ exercise }) => {
  const url = safeMediaUrl(exercise.imageUrl || exercise.library?.imageUrl) || safeMediaUrl(exercise.gifUrl);
  return (
    <span className="wp-thumb" aria-hidden="true">{url ? <img src={url} alt="" loading="lazy" draggable={false} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <Dumbbell size={20} />}</span>
  );
};

function ExerciseRows({ exercises }) {
  return (
    <ol className="wp-today-list">
      {exercises.map((e, i) => (
        <li key={e.planExerciseId ?? e.id ?? i} className="wp-today-row">
          <Thumb exercise={e} />
          <span className="wp-today-text">
            <span className="wp-today-name">
              {supersetOf(exercises, i) ? <span className="wp-group-badge">{supersetOf(exercises, i).label}</span> : null}
              {e.name}
            </span>
            <span className="wp-today-meta">{e.prescription?.summary || 'Sem prescrição'}</span>
          </span>
          {e.status === 'Completed' ? <span className="wp-today-done" aria-label="Concluído">✓</span> : e.status === 'Skipped' ? <span className="wp-today-skip">saltado</span> : null}
        </li>
      ))}
    </ol>
  );
}

export default function TodayScreen({ today, active, view, onStart, onResume, onAbandon, starting, startError, onBack }) {
  const [dayId, setDayId] = useState(today?.dayId ?? null);
  useEffect(() => { setDayId(today?.dayId ?? null); }, [today?.dayId]);

  const suggestedExercises = useMemo(() => today?.exercises || [], [today?.exercises]);
  const hasPlan = today && today.state !== 'NoPlan';
  const progress = useMemo(() => (active && view ? progressOf(view.exercises) : null), [active, view]);
  const minutes = useMemo(() => estimateMinutes(active && view ? view.exercises : suggestedExercises), [active, view, suggestedExercises]);

  if (!active && !hasPlan) {
    return (
      <div className="wp-screen wp-today">
        <TopBar onBack={onBack} title="O meu treino de hoje" />
        <EmptyState title="Ainda não tem treino" message={today?.message || 'Quando o seu treinador lhe atribuir um plano, aparece aqui.'}
          action={<button type="button" className="wp-btn wp-btn-ghost" onClick={onBack}>Voltar</button>} />
      </div>
    );
  }

  const heroExercises = active && view ? view.exercises : suggestedExercises;
  const heroName = active && view ? view.dayName : today.dayName;
  const planTitle = active && view ? view.planTitle : today.planTitle;
  const setsTotal = heroExercises.reduce((n, e) => n + (e.prescription?.setsTarget || 0), 0);
  const nextUp = active && view ? view.exercises.find((e) => e.status === 'Pending' || e.status === 'InProgress') : null;
  const firstWithMedia = heroExercises.find((e) => safeMediaUrl(e.imageUrl || e.library?.imageUrl)) || heroExercises[0];
  const days = today?.days || [];
  const picked = days.find((d) => d.id === dayId);
  const deload = (active && view ? view.deload : today?.deload) || null;

  return (
    <div className="wp-screen wp-today">
      <TopBar onBack={onBack} title="O meu treino de hoje" />

      <div className="wp-today-grid">
        <section className="wp-hero" aria-labelledby="wp-hero-title">
          <div className="wp-hero-media">
            {firstWithMedia ? <MediaBox demo={{ media: [{ kind: 'photo', url: safeMediaUrl(firstWithMedia.imageUrl || firstWithMedia.library?.imageUrl) }].filter((m) => m.url) }} name={firstWithMedia.name} groupLabel={firstWithMedia.library?.groupLabel} compact /> : null}
          </div>
          <div className="wp-hero-body">
            {active ? <Chip tone="good" icon={Flame}>Treino em curso</Chip> : today?.completedTodayExecutionId ? <Chip tone="good" icon={CalendarCheck}>Já treinou hoje</Chip> : null}
            {deload ? <Chip tone="warn" data-testid="deload-badge">Semana leve</Chip> : null}
            <h1 id="wp-hero-title" className="wp-hero-title">{heroName}</h1>
            <p className="wp-hero-plan">{planTitle}</p>
            <div className="wp-chip-row wp-hero-stats">
              <Chip icon={Clock}>~{minutes} min</Chip>
              <Chip icon={Dumbbell}>{plural(heroExercises.length, 'exercício', 'exercícios')}</Chip>
              <Chip icon={Layers}>{plural(setsTotal, 'série', 'séries')}</Chip>
            </div>

            {deload ? (
              <p className="wp-note wp-deload-note" data-testid="deload-note">
                Semana leve: {deload.reduction}. Foi uma pessoa que a marcou — pode desfazer quando quiser.
              </p>
            ) : null}

            {active && progress ? (
              <div className="wp-today-progress" data-testid="resume-progress">
                <div className="wp-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.percent} aria-label="Progresso do treino"><span style={{ width: `${progress.percent}%` }} /></div>
                <p className="wp-muted">{progress.completed + progress.skipped} de {plural(progress.total, 'exercício', 'exercícios')} · {progress.setsLogged === 1 ? '1 série registada' : `${progress.setsLogged} séries registadas`}{view.startedAt ? ` · começou às ${new Date(view.startedAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}` : ''}</p>
                {nextUp ? <p className="wp-today-next">A seguir: <strong>{nextUp.name}</strong></p> : <p className="wp-today-next">Tudo feito: falta concluir o treino.</p>}
              </div>
            ) : (
              <p className="wp-muted wp-why">{whyText(today?.suggestedBy) || ''}</p>
            )}

            {startError ? <p className="wp-error" role="alert" data-testid="start-error">{startError}</p> : null}

            {active ? (
              <>
                <button type="button" className="wp-btn wp-btn-primary wp-btn-xl wp-cta" onClick={onResume} data-testid="continue-workout"><RotateCcw size={22} aria-hidden="true" /> Continuar treino</button>
                <button type="button" className="wp-link-btn wp-danger-link" onClick={onAbandon} data-testid="today-abandon"><Trash2 size={15} aria-hidden="true" /> Abandonar este treino…</button>
              </>
            ) : (
              <button type="button" className="wp-btn wp-btn-primary wp-btn-xl wp-cta" onClick={() => onStart(dayId)} disabled={starting || !heroExercises.length} data-testid="start-workout">
                <Play size={22} fill="currentColor" aria-hidden="true" /> {starting ? 'A começar…' : 'Começar treino'}
              </button>
            )}
          </div>
        </section>

        <section className="wp-today-side" aria-label="Exercícios">
          {!active && days.length > 1 ? (
            <div className="wp-days" role="radiogroup" aria-label="Escolher outro dia">
              <span className="wp-chip-label">Outro dia</span>
              {days.map((d) => (
                <button key={d.id} type="button" role="radio" aria-checked={d.id === dayId} className={`wp-rpe-chip wp-day ${d.id === dayId ? 'is-on' : ''}`} onClick={() => setDayId(d.id)} data-testid={`day-${d.id}`}>
                  {d.name}{d.suggested ? ' ★' : ''}
                </button>
              ))}
            </div>
          ) : null}
          {!active && picked && picked.id !== today.dayId ? (
            <p className="wp-note">Vai treinar <strong>{picked.name}</strong> ({plural(picked.exerciseCount, 'exercício', 'exercícios')}). Os detalhes mostram-se ao começar.</p>
          ) : <ExerciseRows exercises={heroExercises} />}
        </section>
      </div>
    </div>
  );
}

export const TopBar = ({ onBack, title, right }) => (
  <header className="wp-topbar">
    <button type="button" className="wp-icon-btn" onClick={onBack} aria-label="Voltar"><ArrowLeft size={22} aria-hidden="true" /></button>
    <span className="wp-topbar-title">{title}</span>
    <span className="wp-topbar-right">{right}</span>
  </header>
);

